import React from 'react';
import PropTypes from 'prop-types';
import InitialImageContext from './Context';
import {getImageUrlFromName} from 'virtual-tour-viewer/src/utils/UIHelper';
import {getStartImageName} from 'virtual-tour-viewer/src/utils/sphereDataHelper';
import {isWebpSupported} from 'virtual-tour-viewer/src/webApiUtils/networkDiagnostics';

// Conver image to base64 string. From here https://medium.com/front-end-weekly/fetching-images-with-the-fetch-api-fb8761ed27b2
const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    let bytes = [].slice.call(new Uint8Array(buffer));

    bytes.forEach((b) => (binary += String.fromCharCode(b)));

    return window.btoa(binary);
};

/**
 * Load the initial q3 image for the tour in a client-side provider, so that the image can be loaded in parallel with other data needed to start the tour
 */
class InitialImageProvider extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            image: null,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        let nextState = prevState;
        if (!nextState.imagePromise && nextProps.tourData) {
            const startImageName = getStartImageName(
                nextProps.tourData,
                nextProps.defaultStartSphereId,
                nextProps.useMediaGateway
            );
            const doesSupportWebp = isWebpSupported(nextProps.useMediaGateway);
            let imageName = getImageUrlFromName(startImageName, 'q3', nextProps.useMediaGateway);
            imageName = doesSupportWebp ? imageName.replace(/jpeg|jpg/g, '.webp') : imageName;
            nextState.imagePromise = fetch(imageName);
        }
        return nextState;
    }

    componentDidMount() {
        if (this.state.imagePromise) {
            const startTime = Date.now();
            this.state.imagePromise.then((data) => {
                if (data.status === 200) {
                    const loadTime = Date.now() - startTime;
                    data.blob().then((blob) => {
                        // Use blob.arrayBuffer if possible. Otherwise, make a new response and convert that to an arrayBuffer
                        // At time of writing, blog.arrayBuffer is not available on Safari and Response is not available on IE
                        const bufferPromise = blob.arrayBuffer
                            ? blob.arrayBuffer()
                            : new Response(blob).arrayBuffer();
                        bufferPromise.then((buffer) => {
                            this.setState({
                                ...this.state,
                                loaded: true,
                                image: {
                                    response: `data:${blob.type};base64,${arrayBufferToBase64(
                                        buffer
                                    )}`,
                                    loadTime,
                                },
                            });
                        });
                    });
                } else {
                    this.setState({
                        ...this.state,
                        loaded: false,
                        image: null,
                    });
                }
            });
        }
    }

    componentDidUpdate() {}

    render() {
        // TODO display an error if the image fails to load (MEDIA-1150)
        return (
            <InitialImageContext.Provider value={this.state.image}>
                {this.props.children}
            </InitialImageContext.Provider>
        );
    }
}

InitialImageProvider.propTypes = {
    // TODO: Define the shape of tourData in the viewerPackage such that it can be reused here.
    // eslint-disable-next-line react/forbid-prop-types
    tourData: PropTypes.object.isRequired,
    defaultStartSphereId: PropTypes.string.isRequired,
    useMediaGateway: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
};

export default InitialImageProvider;
